import React, { useState, useEffect } from "react";
import { useForm, FormProvider, useFieldArray } from "react-hook-form";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { trpc } from "@/lib/trpc";
import { estimateFormSchema } from "@/pages/estimating/calculator/EstimatingCalculatorView";
import type { EstimateFormValues } from "@/pages/estimating/calculator/EstimatingCalculatorView";

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, Calculator, Save, Plus, Trash2, Printer, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import EstimatingComponentTab from "@/pages/estimating/calculator/components/EstimatingComponentTab";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import PrintDialog from "@/components/shared/PrintDialog";

interface CostEstimateSpreadsheetProps {
  quotationDetailId: string;
  quotationId?: string;
  quantity?: number;
}

export function CostEstimateSpreadsheet({ quotationDetailId, quotationId, quantity }: CostEstimateSpreadsheetProps) {
  const queryClient = useQueryClient();

  // Print MOQ states
  const [showMoqDialog, setShowMoqDialog] = useState(false);
  const [moqInput, setMoqInput] = useState(quantity ? `${quantity}, ${quantity * 2}, ${quantity * 3}` : "1000, 2000, 3000, 5000");
  const [isSimulating, setIsSimulating] = useState(false);
  const [printData, setPrintData] = useState<any>(null);
  const [showPrint, setShowPrint] = useState(false);
  const [printType, setPrintType] = useState<'cost-breakdown-moq' | 'customer-moq-quotation'>('cost-breakdown-moq');

  // 1. Fetch real data from QuotationDetail & Product
  const { data: draftData, isLoading: isLoadingDraft } = useQuery({
    ...trpc.estimating.getDraft.queryOptions({ quotationDetailId }),
    enabled: !!quotationDetailId,
  });

  const defaultValues: EstimateFormValues = {
    estimateCode: "EST-" + Date.now() + "-" + quotationDetailId.substring(0, 4).toUpperCase(),
    orderQty: quantity || 1000,
    components: [
      {
        name: "Sản phẩm chính",
        outUp: 1,
        materials: [],
        routings: []
      }
    ]
  };

  const form = useForm<EstimateFormValues>({
    resolver: zodResolver(estimateFormSchema),
    defaultValues,
  });

  // 2. Auto-hydrate form when draft data arrives
  useEffect(() => {
    if (draftData) {
      const hasValidCostEstimate = draftData.costEstimate &&
        draftData.costEstimate.components &&
        draftData.costEstimate.components.length > 0 &&
        draftData.costEstimate.components.some((c: any) => (c.materials?.length > 0) || (c.routings?.length > 0));

      if (hasValidCostEstimate) {
        // Hydrate from existing Cost Estimate
        const mappedComponents = draftData.costEstimate.components.map((c: any) => ({
          id: c.id,
          name: c.name,
          outUp: c.outUp || 1,
          materials: (c.materials || []).map((m: any) => ({
            id: m.id,
            materialId: m.materialId,
            materialName: m.materialName,
            unitPrice: Number(m.unitPrice || 0),
            setupWasteQty: m.setupWasteQty ?? 50,
            runWastePct: Number(m.runWastePct ?? 3),
          })),
          routings: (c.routings || []).map((r: any) => ({
            id: r.id,
            processName: r.processName,
            processType: r.processType || 'PRINTING',
            batchCost: Number(r.batchCost || r.minBatchCost || 0),
            moq: Number(r.moq || 0),
            unitCost: Number(r.unitCost || 0),
            setupWasteQty: r.setupWasteQty ?? 50,
            runWastePct: Number(r.runWastePct ?? 2),
          }))
        }));
        
        form.reset({
          estimateCode: draftData.costEstimate.estimateCode,
          orderQty: draftData.costEstimate.orderQty || draftData.quotationDetail?.quantity || 1000,
          components: mappedComponents
        });
      } else if (draftData.product) {
        // AUTO BÓC TÁCH TỪ PRODUCT (BOM & Routing Master)
        const p = draftData.product;
        const d = draftData.decomposedData;

        let mappedMaterials: any[] = [];
        let mappedRoutings: any[] = [];

        // 1. Vật tư: Ưu tiên productMaterials -> decomposedData -> product.material
        if (p.productMaterials && p.productMaterials.length > 0) {
          mappedMaterials = p.productMaterials.map((pm: any) => ({
            materialId: pm.materialId,
            materialName: pm.material?.name || 'Vật tư',
            unitPrice: Number(pm.material?.unitPrice || 0),
            setupWasteQty: 50,
            runWastePct: 3,
          }));
        } else if (d?.components?.[0]?.materials && d.components[0].materials.length > 0) {
          mappedMaterials = d.components[0].materials.map((m: any) => ({
            materialId: m.materialId,
            materialName: m.materialName,
            unitPrice: Number(m.unitPrice || 0),
            setupWasteQty: m.setupWasteQty ?? 50,
            runWastePct: m.runWastePct ?? 3,
          }));
        } else if (p.material) {
          mappedMaterials = [{
            materialId: p.material.id,
            materialName: p.material.name,
            unitPrice: Number(p.material.unitPrice || 2500),
            setupWasteQty: 50,
            runWastePct: 3,
          }];
        }

        // 2. Công đoạn: Ưu tiên productProcesses -> decomposedData
        if (p.productProcesses && p.productProcesses.length > 0) {
          mappedRoutings = p.productProcesses.map((pp: any) => ({
            processName: pp.process?.name || 'Công đoạn',
            processType: pp.process?.group || 'PRINTING',
            batchCost: Number(pp.batchCost ?? pp.process?.defaultBatchCost ?? pp.process?.setupCost ?? 0),
            moq: Number(pp.moq || 0),
            unitCost: Number(pp.unitCost ?? pp.process?.defaultUnitCost ?? pp.process?.unitCost ?? 0),
            setupWasteQty: pp.setupWasteQty ?? 50,
            runWastePct: Number(pp.runWastePercent ?? 2),
          }));
        } else if (d?.components?.[0]?.routings && d.components[0].routings.length > 0) {
          mappedRoutings = d.components[0].routings.map((r: any) => ({
            processName: r.processName,
            processType: r.group || 'PRINTING',
            batchCost: Number(r.setupCost || 0),
            moq: Number(r.moq || 0),
            unitCost: Number(r.unitCost || 0),
            setupWasteQty: r.setupWasteQty ?? 50,
            runWastePct: Number(r.runWastePct ?? 2),
          }));
        }

        form.reset({
          estimateCode: "EST-" + Date.now() + "-" + quotationDetailId.substring(0, 4).toUpperCase(),
          orderQty: draftData.quotationDetail?.quantity || 1000,
          components: [
            {
              name: p.productName || "Sản phẩm chính",
              outUp: p.outs || 1,
              materials: mappedMaterials,
              routings: mappedRoutings
            }
          ]
        });
      }
    }
  }, [draftData, form, quotationDetailId]);

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "components"
  });

  const [activeTab, setActiveTab] = useState("comp-0");

  const watchAllFields = form.watch();

  // Call TRPC to simulate
  const { data: simulationResultArray, isFetching } = useQuery({
    ...trpc.estimating.simulateCalculation.queryOptions({
      orderQtys: [watchAllFields.orderQty],
      components: watchAllFields.components,
    }),
    enabled: form.formState.isValid,
    refetchOnWindowFocus: false,
    staleTime: 100, // Very short stale time for reactivity
  });

  const simulationResult = simulationResultArray?.[0];

  const saveMutation = useMutation({
    ...trpc.estimating.saveCostEstimate.mutationOptions(),
    onSuccess: () => {
      toast.success("Đã lưu bảng tính giá và cập nhật vào chi tiết báo giá");
      queryClient.invalidateQueries({ queryKey: [["sales", "quotations", "getById"]] });
    },
    onError: (error: any) => {
      toast.error(error.message || "Lỗi khi lưu bảng tính");
    }
  });



  const onSubmit = (data: EstimateFormValues) => {
    saveMutation.mutate({
      ...data,
      quotationDetailId
    });
  };

  const handlePrintMOQ = async (type: 'cost-breakdown-moq' | 'customer-moq-quotation') => {
    // In real app, you would pass the form components to simulateMultipleMOQs
    // For now we mock it as before
    setIsSimulating(true);
    setTimeout(() => {
      const quantities = moqInput
        .split(",")
        .map(s => parseInt(s.trim(), 10))
        .filter(n => !isNaN(n) && n > 0);
        
      if (quantities.length === 0) {
        toast.error("Vui lòng nhập ít nhất 1 số lượng hợp lệ");
        setIsSimulating(false);
        return;
      }
      
      const results = quantities.map(qty => {
        // Mock calculations
        const tc = 2500000 + (qty * 1200);
        return {
          quantity: qty,
          totalMaterialCost: 1500000 + (qty * 800),
          totalProcessCost: 1000000 + (qty * 400),
          totalFixedCost: 500000,
          marginPercent: 25,
          marginValue: tc * 0.25,
          suggestedPrice: tc * 1.25,
          unitPrice: (tc * 1.25) / qty,
        };
      });
      
      setPrintData({ items: results, productName: "Sản phẩm Báo giá" });
      setPrintType(type);
      setShowMoqDialog(false);
      setShowPrint(true);
      setIsSimulating(false);
    }, 1000);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center bg-slate-50 p-4 rounded-lg border">
        <div>
          <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
            <Calculator className="h-5 w-5 text-indigo-600" /> 
            Bảng chiết giá
          </h3>
          <p className="text-sm text-slate-500">Cấu hình thành phần và quy trình để tính chi phí.</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setShowMoqDialog(true)} variant="outline" className="border-indigo-200 text-indigo-700 hover:bg-indigo-50">
            <Printer className="mr-2 h-4 w-4" />
            In bảng chiết giá
          </Button>
          <Button type="button" onClick={form.handleSubmit(onSubmit)} disabled={saveMutation.isPending} className="bg-indigo-600 hover:bg-indigo-700 text-white">
            {saveMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            Lưu bảng tính
          </Button>

        </div>
      </div>

      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 xl:col-span-8">
          <FormProvider {...form}>
            <form className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Thông tin chung</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="estimateCode"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Mã Báo Giá Nội Bộ</FormLabel>
                        <FormControl>
                          <Input {...field} readOnly className="bg-slate-50" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="orderQty"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Số lượng (Thành phẩm)</FormLabel>
                        <FormControl>
                          <Input 
                            type="number" 
                            {...field} 
                            onChange={e => field.onChange(parseInt(e.target.value) || 0)} 
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">Bình bài & Công đoạn</CardTitle>
                    <Button 
                      type="button" 
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        const newIndex = fields.length;
                        append({ name: `Thành phần mới`, outUp: 1, materials: [], routings: [] });
                        setActiveTab(`comp-${newIndex}`);
                      }}
                    >
                      <Plus className="mr-1.5 h-3.5 w-3.5" /> Thêm Thành phần
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList className="mb-4">
                      {fields.map((field, index) => (
                        <TabsTrigger key={field.id} value={`comp-${index}`} className="flex gap-2">
                          {watchAllFields.components?.[index]?.name || `Component ${index + 1}`}
                          {fields.length > 1 && (
                            <Trash2 
                              className="h-3 w-3 text-red-400 hover:text-red-600" 
                              onClick={(e) => {
                                e.stopPropagation();
                                remove(index);
                                if (activeTab === `comp-${index}`) {
                                  setActiveTab("comp-0");
                                }
                              }}
                            />
                          )}
                        </TabsTrigger>
                      ))}
                    </TabsList>
                    
                    {fields.map((field, index) => (
                      <TabsContent key={field.id} value={`comp-${index}`}>
                        <EstimatingComponentTab componentIndex={index} />
                      </TabsContent>
                    ))}
                  </Tabs>
                </CardContent>
              </Card>
            </form>
          </FormProvider>
        </div>

        <div className="col-span-12 xl:col-span-4">
          <Card className="sticky top-8 border-indigo-100 shadow-md">
            <CardHeader className="bg-indigo-50/50 border-b border-indigo-100 pb-4">
              <CardTitle className="flex items-center gap-2 text-indigo-900 text-lg">
                <Calculator className="h-5 w-5 text-indigo-600" />
                Tổng Hợp Chi Phí
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6 relative">
              {isFetching && <div className="absolute inset-0 bg-white/60 flex items-center justify-center z-10 backdrop-blur-[1px]"><Loader2 className="h-8 w-8 animate-spin text-indigo-600" /></div>}
              
              {simulationResult ? (
                <div className="space-y-4">
                  <div className="flex justify-between items-center py-2 border-b border-dashed border-slate-200">
                    <span className="text-slate-600">Vật tư:</span>
                    <span className="font-semibold text-slate-700">{simulationResult.summary.totalMaterialCost.toLocaleString()} đ</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-dashed border-slate-200">
                    <span className="text-slate-600">Gia công:</span>
                    <span className="font-semibold text-slate-700">{simulationResult.summary.totalProcessCost.toLocaleString()} đ</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-slate-200">
                    <span className="text-slate-600">Lợi nhuận (Margin {simulationResult.summary.marginPercent}%):</span>
                    <span className="font-semibold text-emerald-600">+{simulationResult.summary.marginValue.toLocaleString()} đ</span>
                  </div>
                  <div className="flex justify-between items-center py-4 bg-indigo-50 -mx-6 px-6 border-b border-indigo-100">
                    <span className="font-bold text-lg text-indigo-950">TỔNG CỘNG:</span>
                    <span className="font-bold text-xl text-indigo-600">{simulationResult.summary.suggestedPrice.toLocaleString()} đ</span>
                  </div>
                  <div className="flex justify-between items-center pt-2">
                    <span className="text-slate-600">Đơn giá / SP:</span>
                    <span className="font-bold text-3xl text-indigo-700">{simulationResult.summary.unitPrice.toLocaleString()} đ</span>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-slate-400 flex flex-col items-center">
                  <Calculator className="h-12 w-12 text-slate-200 mb-4" />
                  Đang đợi dữ liệu tính toán...
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* MOQ Dialog */}
      <Dialog open={showMoqDialog} onOpenChange={setShowMoqDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>In Bảng Chiết Giá Sản Phẩm (MOQ)</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <label className="text-sm font-medium mb-2 block">
              Nhập các mốc số lượng (cách nhau bằng dấu phẩy)
            </label>
            <Input 
              value={moqInput} 
              onChange={e => setMoqInput(e.target.value)} 
              placeholder="Ví dụ: 1000, 2000, 5000" 
            />
            <p className="text-xs text-muted-foreground mt-2">
              Hệ thống sẽ chạy giả lập chi phí (What-if analysis) cho từng mốc số lượng này và xuất ra bản in để so sánh.
            </p>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowMoqDialog(false)} className="sm:mr-auto">Hủy</Button>
            <Button variant="outline" onClick={() => handlePrintMOQ('cost-breakdown-moq')} disabled={isSimulating} className="border-indigo-200 text-indigo-700 hover:bg-indigo-50">
              {isSimulating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Printer className="mr-2 h-4 w-4" />}
              In Nội Bộ (Bóc tách)
            </Button>
            <Button onClick={() => handlePrintMOQ('customer-moq-quotation')} disabled={isSimulating} className="bg-indigo-600 hover:bg-indigo-700 text-white">
              {isSimulating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Printer className="mr-2 h-4 w-4" />}
              In Cho Khách (Báo giá)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Print Overlay */}
      <PrintDialog 
        open={showPrint} 
        onClose={() => setShowPrint(false)} 
        type={printType} 
        data={printData} 
      />
    </div>
  );
}
